import { useEffect, useState } from 'react'
import { Trash, Lock, Check, X, Clock, Users, ListBullets, ArrowsClockwise } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useDeletionApprovalsStore } from '@/stores/deletionApprovalsStore'
import { usePermission } from '@/hooks/usePermission'
import { cardChromeClass, iconChipClass, iconChipAmberClass } from '@/lib/styleKit'

/**
 * Approver queue for a Representative's deletion requests. A Rep may add
 * contacts to any team list and take them off again, but deleting -- a
 * contact for good, or a whole list -- waits here for a Manager/Admin/Owner.
 * Approving performs the deletion right away. Mirrors CrmExportApprovalsSection.
 */
export default function DeletionApprovalsSection() {
  const { pending, loading, fetchPending, approve, reject } = useDeletionApprovalsStore()
  const { allowed, scope } = usePermission('contacts', 'hard_delete')
  // A Rep's own level is "gated" (truthy, so `allowed` alone would let them
  // in) -- "gated" means "may request a deletion", never "may approve one".
  const canApprove = allowed && scope === 'all'
  const [actingOn, setActingOn] = useState<number | null>(null)

  useEffect(() => {
    if (!canApprove) return
    fetchPending().catch(() => {})
  }, [canApprove, fetchPending])

  const act = async (fn: (id: number) => Promise<void>, id: number) => {
    setActingOn(id)
    try {
      await fn(id)
    } catch {
      // Error already toasted by the store.
    } finally {
      setActingOn(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Deletion Approvals
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          {canApprove
            ? 'Reps need your approval before contacts or a contact list are deleted. Approving deletes them right away; contacts still in an active campaign are skipped.'
            : 'Your role determines what you can see and do in this account.'}
        </p>
      </div>

      {!canApprove ? (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipAmberClass}>
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <CardTitle>No access</CardTitle>
                <CardDescription>Your role doesn't include reviewing deletion requests</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className={iconChipClass}>
                  <Trash className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                </div>
                <div>
                  <CardTitle>Pending requests</CardTitle>
                  <CardDescription>Contacts and lists a Representative asked to delete</CardDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                loading={loading}
                onClick={() => fetchPending().catch(() => {})}
                icon={<ArrowsClockwise className="w-3.5 h-3.5" weight="regular" />}
                aria-label="Refresh pending requests"
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pending.length === 0 ? (
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                {loading ? 'Loading…' : 'No pending deletion requests.'}
              </p>
            ) : (
              <div className="space-y-2">
                {pending.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] flex-wrap"
                  >
                    <div className="min-w-0">
                      <p
                        className="text-foreground dark:text-[#f7f8f8] truncate"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {req.kind === 'list' ? `Delete list "${req.summary}"` : `Delete ${req.summary}`}
                      </p>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] flex items-center gap-2.5 flex-wrap mt-0.5"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        <span className="flex items-center gap-1.5">
                          {req.kind === 'list' ? <ListBullets className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                          {req.kind === 'list'
                            ? `List with ${req.item_count.toLocaleString()} contact${req.item_count === 1 ? '' : 's'}`
                            : `${req.item_count.toLocaleString()} contact${req.item_count === 1 ? '' : 's'}`}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          Requested by {req.requested_by_name}
                          {req.requested_at ? ` on ${new Date(req.requested_at).toLocaleString()}` : ''}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={actingOn === req.id}
                        icon={<X className="w-3.5 h-3.5 text-destructive" />}
                        onClick={() => act(reject, req.id)}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        loading={actingOn === req.id}
                        icon={<Check className="w-3.5 h-3.5" />}
                        onClick={() => act(approve, req.id)}
                      >
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
